import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Typography,
  Link as MuiLink,
  FormControlLabel,
  Switch
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import React from 'react'
import { type PrioritizationReportModel } from './PrioritizationReport.types'
import * as XLSX from 'xlsx'

interface TableViewProps {
  reportData: PrioritizationReportModel[]
  loading: boolean
}

export function PrioritizationReportTableView (props: TableViewProps): JSX.Element {
  const { reportData, loading } = props
  const [groupByFacility, setGroupByFacility] = React.useState(true)

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (dateString == null) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  // Calculate age from DOB
  const calculateAge = (dobString: string): number => {
    const dob = new Date(dobString)
    const today = new Date()
    let age = today.getFullYear() - dob.getFullYear()
    const monthDiff = today.getMonth() - dob.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--
    }
    return age
  }

  // Extract first facility from priority locations
  const getFirstFacility = (priorityLocations: string | null): string => {
    if (priorityLocations == null || priorityLocations.length === 0) return 'Unknown Facility'
    const firstLocation = priorityLocations.split(', ')[0]
    // Format is "Provider Name - Facility Name", return the full string as facility identifier
    return firstLocation
  }

  // Group children by their first choice facility (if grouping is enabled)
  const groupedData = groupByFacility
    ? reportData.reduce<Record<string, typeof reportData>>((acc, child) => {
      const firstFacility = getFirstFacility(child.priorityLocations)
      if (acc[firstFacility] == null) {
        acc[firstFacility] = []
      }
      acc[firstFacility].push(child)
      return acc
    }, {})
    : { 'All Children': reportData }

  // Sort facility names alphabetically
  const sortedFacilities = Object.keys(groupedData).sort()

  // Export to Excel
  const handleExportToExcel = (): void => {
    const headers = groupByFacility
      ? [
          'First Choice Facility',
          'Rank',
          'App Date',
          'Child Name',
          'Age',
          'FPL %',
          'Priority Score',
          'IEP',
          'Foster',
          'Homeless',
          'SNAP',
          'Other Choices'
        ]
      : [
          'Rank',
          'App Date',
          'Child Name',
          'Age',
          'FPL %',
          'Priority Score',
          'IEP',
          'Foster',
          'Homeless',
          'SNAP',
          'Priority Locations'
        ]

    const data: Array<Array<string | number>> = []

    if (groupByFacility) {
      sortedFacilities.forEach(facilityName => {
        const facilityChildren = groupedData[facilityName]
        facilityChildren.forEach((row, index) => {
          const otherLocations = row.priorityLocations?.split(', ').slice(1).join(', ') ?? ''
          data.push([
            facilityName,
            index + 1,
            formatDate(row.appDate),
            row.childName,
            calculateAge(row.dob),
            row.fpl != null ? `${row.fpl}%` : 'N/A',
            row.priorityScore,
            row.iep ? 'Yes' : 'No',
            row.foster ? 'Yes' : 'No',
            row.homeless ? 'Yes' : 'No',
            row.snap ? 'Yes' : 'No',
            otherLocations.length > 0 ? otherLocations : '-'
          ])
        })
      })
    } else {
      reportData.forEach((row, index) => {
        data.push([
          index + 1,
          formatDate(row.appDate),
          row.childName,
          calculateAge(row.dob),
          row.fpl != null ? `${row.fpl}%` : 'N/A',
          row.priorityScore,
          row.iep ? 'Yes' : 'No',
          row.foster ? 'Yes' : 'No',
          row.homeless ? 'Yes' : 'No',
          row.snap ? 'Yes' : 'No',
          row.priorityLocations ?? '-'
        ])
      })
    }

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data])
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Prioritization Report')

    const date = new Date().toISOString().split('T')[0]
    XLSX.writeFile(workbook, `Prioritization_Report_${date}.xlsx`)
  }

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Switch
              checked={groupByFacility}
              onChange={(e) => { setGroupByFacility(e.target.checked) }}
              disabled={loading}
            />
          }
          label="Group by First Choice Facility"
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExportToExcel}
          sx={{ bgcolor: '#367575', '&:hover': { bgcolor: '#2A5C5C' } }}
        >
          Download Excel
        </Button>
      </Box>

      {/* Grouped Tables by Facility (or single table if not grouped) */}
      {sortedFacilities.map((facilityName, facilityIdx) => {
        const facilityChildren = groupedData[facilityName]
        let rankCounter = 0 // Track rank within this facility group

        return (
          <Box key={facilityIdx} sx={{ mb: 4 }}>
            {/* Facility Header (only show if grouping is enabled) */}
            {groupByFacility && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    bgcolor: '#367575',
                    color: 'white',
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  {facilityName}
                </Typography>
                <Chip
                  label={`${facilityChildren.length} ${facilityChildren.length === 1 ? 'child' : 'children'}`}
                  sx={{ bgcolor: '#E3F2FD', fontWeight: 'bold' }}
                />
                {facilityChildren[0]?.providerTotalCapacity != null && (
                  <>
                    <Chip
                      label={`Capacity: ${facilityChildren[0].providerTotalCapacity}`}
                      sx={{ bgcolor: '#F5F5F5', fontWeight: 'bold' }}
                    />
                    <Chip
                      label={`Active: ${facilityChildren[0].providerActiveSeats ?? 0}`}
                      sx={{ bgcolor: '#E8F5E9', fontWeight: 'bold' }}
                    />
                    <Chip
                      label={`Available: ${(facilityChildren[0].providerTotalCapacity ?? 0) - (facilityChildren[0].providerActiveSeats ?? 0)}`}
                      sx={{ bgcolor: '#FFF3E0', fontWeight: 'bold' }}
                    />
                  </>
                )}
              </Box>
            )}

            {/* Table for this facility */}
            <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 2 }}>
              <Table size="small" className="report-table">
                <TableHead>
                  <TableRow >
                    <TableCell sx={{ width: '60px' }}>
                      Rank
                    </TableCell>
                    <TableCell >
                      App Date
                    </TableCell>
                    <TableCell>
                      Child Name
                    </TableCell>
                    <TableCell>
                      Age
                    </TableCell>
                    <TableCell>
                      FPL %
                    </TableCell>
                    <TableCell>
                      Score
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      IEP
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      Foster
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      Homeless
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      SNAP
                    </TableCell>
                    <TableCell>
                      {groupByFacility ? 'Other Choices' : 'Priority Locations'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facilityChildren.map((row) => {
                    rankCounter++
                    // Get other priority locations (exclude first one if grouped, show all if not)
                    const locations = groupByFacility
                      ? row.priorityLocations?.split(', ').slice(1).join(', ') ?? ''
                      : row.priorityLocations ?? ''

                    return (
                      <TableRow
                        key={row.childID}
                        sx={{
                          borderBottom: '1px solid #E1E1E1',
                          '&:hover': { bgcolor: '#F9F9F9' }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          #{rankCounter}
                        </TableCell>
                        <TableCell >
                          {formatDate(row.appDate)}
                        </TableCell>
                        <TableCell >
                          <MuiLink
                            href={`/CareNetworkManagement/Details/${row.childID}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`View details for ${row.childName} (opens in new window)`}
                            sx={{
                              color: '#367575',
                              fontWeight: 'medium',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {row.childName}
                            <OpenInNewIcon sx={{ fontSize: '14px' }} />
                          </MuiLink>
                        </TableCell>
                        <TableCell>
                          {calculateAge(row.dob)}
                        </TableCell>
                        <TableCell>
                          {row.fpl != null ? `${row.fpl}%` : 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {row.priorityScore}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          {row.iep && (
                            <CheckCircleIcon aria-label="Yes" sx={{ color: '#367575', fontSize: '18px' }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          {row.foster && (
                            <CheckCircleIcon aria-label="Yes" sx={{ color: '#367575', fontSize: '18px' }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          {row.homeless && (
                            <CheckCircleIcon aria-label="Yes" sx={{ color: '#FF9800', fontSize: '18px' }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          {row.snap && (
                            <CheckCircleIcon aria-label="Yes" sx={{ color: '#367575', fontSize: '18px' }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ color: '#666', fontSize: '11px', maxWidth: 250 }}>
                          {locations.length > 0 ? locations : '-'}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )
      })}
    </Box>
  )
}
